import { AccountProvenance, type BankAccount } from '../types/bank-account'

export const ConnectivityType = {
  OpenBanking: 1,
  ManualUpload: 0,
  API: 2,
  SFTP: 6,
  Email: 7,
  SWIFT: 8,
} as const

export type ConnectivityValue = (typeof ConnectivityType)[keyof typeof ConnectivityType]

export interface ConnectivityOption {
  value: ConnectivityValue
  label: string
  isOpenBanking: boolean
}

export const CONNECTIVITY_OPTIONS: ConnectivityOption[] = [
  { value: ConnectivityType.OpenBanking, label: 'Open Banking', isOpenBanking: true },
  { value: ConnectivityType.ManualUpload, label: 'Manual Upload', isOpenBanking: false },
  { value: ConnectivityType.SWIFT, label: 'Swift', isOpenBanking: false },
  { value: ConnectivityType.API, label: 'API', isOpenBanking: false },
  { value: ConnectivityType.SFTP, label: 'SFTP', isOpenBanking: false },
  { value: ConnectivityType.Email, label: 'Email', isOpenBanking: false },
]

export function isOpenBankingConnectivity(value: ConnectivityValue): boolean {
  return value === ConnectivityType.OpenBanking
}

/** Whether the edit wizard may change this account's connection (UC-094). An account
 * created on Open Banking never may, whatever the backend's `connectivityEditable` says. */
export function canChangeConnectivity(account: BankAccount): boolean {
  const isOpenBankingAccount = isOpenBankingConnectivity(connectivityValueFromLabel(account.connectivity))
    || account.accountProvenance === AccountProvenance.OpenBanking
  return !isOpenBankingAccount && account.connectivityEditable === true
}

/** Maps the grid's display string (`BankAccount.connectivity`) back to the
 * numeric `ConnectivityValue` used by the wizard's connectivity selector. */
export function connectivityValueFromLabel(label: string | undefined): ConnectivityValue {
  switch (label) {
    case 'Open Banking': return ConnectivityType.OpenBanking
    case 'API': return ConnectivityType.API
    case 'SFTP': return ConnectivityType.SFTP
    case 'Email': return ConnectivityType.Email
    case 'Swift':
    case 'SWIFT': return ConnectivityType.SWIFT
    default: return ConnectivityType.ManualUpload // "Manual Upload", "FTP" (legacy), and unknown fall back to Manual
  }
}
